import { useCallback, useEffect, useRef, useState } from "react";
import { EventType } from "@azure/msal-browser";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import AppButton from "../ui/AppButton";
import { msalInstance } from "./msalConfig";

// Idle timeout: warn after warningDelay ms, auto-logout after timeout ms.
function AutoLogout({ timeout = 30 * 60 * 1000, warningDelay =  5 * 60 * 1000 }) {
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  // Track sign-in state off MSAL events so it works on the login page too.
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => msalInstance.getAllAccounts().length > 0,
  );

  useEffect(() => {
    const callbackId = msalInstance.addEventCallback((message) => {
      if (
        message.eventType === EventType.LOGIN_SUCCESS ||
        message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        message.eventType === EventType.LOGOUT_SUCCESS ||
        message.eventType === EventType.LOGOUT_END
      ) {
        setIsAuthenticated(msalInstance.getAllAccounts().length > 0);
      }
    });

    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    window.location.href = process.env.REACT_APP_SSO_LOGOUT_REDIRECT_URL;
  }, []);

  const resetTimer = useCallback(() => {
    clearTimeout(timeoutRef.current);
    clearTimeout(warningTimeoutRef.current);
    setShowWarning(false);

    warningTimeoutRef.current = setTimeout(() => {
      setSecondsLeft(Math.round((timeout - warningDelay) / 1000));
      setShowWarning(true);
    }, warningDelay);

    timeoutRef.current = setTimeout(handleLogout, timeout);
  }, [timeout, warningDelay, handleLogout]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const activityEvents = ["click", "keydown", "mousedown", "touchstart"];
    activityEvents.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(warningTimeoutRef.current);
      activityEvents.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [isAuthenticated, resetTimer]);

  // Live countdown shown in the warning dialog
  useEffect(() => {
    if (!showWarning) return undefined;

    const interval = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [showWarning]);

  if (!isAuthenticated || !showWarning) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <Dialog
      open={showWarning}
      onClose={resetTimer}
      PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", width: 380 } }}
    >
      <div
        style={{
          background: "var(--app-ai-assistant-gradient)",
          padding: "28px 24px 20px",
          textAlign: "center",
          color: "#fff",
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            margin: "0 auto 12px",
            borderRadius: "50%",
            background: "var(--app-ai-assistant-gradient)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AccessTimeFilledIcon sx={{ fontSize: 30, color: "#fff" }} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 700 }}>Are you still there?</div>
      </div>

      <DialogContent style={{ textAlign: "center", padding: "24px" }}>
        <p style={{ margin: 0, color: "#4b5563", fontSize: 14, lineHeight: 1.5 }}>
          You've been inactive for a while. For your security, you'll be signed out automatically in
        </p>

        <div
          style={{
            margin: "16px 0",
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: "1px",
            color: "var(--app-ai-assistant-primary, #253978)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
        </div>

        <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "8px" }}>
          <AppButton variant="outlined" onClick={handleLogout}>
            Logout Now
          </AppButton>
          <AppButton variant="contained" onClick={resetTimer}>
            Stay Logged In
          </AppButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AutoLogout;
